import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { apiUrl } from "../../config/api";
import { useBreakpoint } from "../../utils/useMediaQuery";

const EMPTY_STAFF_ROW = {
  position: "",
  regime: "",
  time_from: "",
  time_to: "",
  count: 0,
  salary: 0,
  cash_salary: 0,
  salary_card: 0,
  cash_total: 0,
  card_total: 0,
  total: 0,
  cash_paid: 0,
  card_paid: 0,
  cash_left: 0,
  card_left: 0,
  comment: "",
  date_cash: "",
};

const createEmptyStaffRow = () => ({ ...EMPTY_STAFF_ROW });

const staffRowHasContent = (row = {}) => {
  if (!row) return false;
  const textFields = ["position", "regime", "time_from", "time_to", "comment", "date_cash"];
  if (textFields.some((f) => String(row[f] || "").trim().length > 0)) return true;
  const numericFields = ["count", "salary", "cash_salary", "salary_card", "cash_paid", "card_paid"];
  if (numericFields.some((f) => Number(row[f]) > 0)) return true;
  return false;
};

export default function StaffSheet({
  data,
  onChange,
  printMeta,
  // по умолчанию считаем, что вы админ, пока нет системы ролей
  isAdmin = true,
  onEditPositions,
  onEditRegimes,
  readOnlyLocked = false,
  isSuper = false,
  advancesComment = "",
  onAdvancesCommentChange,
}) {
  const rows = useMemo(() => (Array.isArray(data) ? data : data?.sheets?.staff || []), [data]);
  const [draftRow, setDraftRow] = useState(createEmptyStaffRow());
  const rowsWithDraft = useMemo(() => [...rows, { ...draftRow, _isDraft: true }], [rows, draftRow]);
  const isCompact = useBreakpoint('lg');
  const [advancesDraft, setAdvancesDraft] = useState(String(advancesComment || ""));

  useEffect(() => {
    setAdvancesDraft(String(advancesComment || ""));
  }, [advancesComment]);

  const emitAdvancesChange = (value, opts) => {
    if (typeof onAdvancesCommentChange === 'function') {
      onAdvancesCommentChange(value, opts);
    }
  };

  // Справочники: должности теперь приходят с backend /api/positions (единый словарь)
  const [positionsList, setPositionsList] = useState([]);
  useEffect(()=>{
    const token = (typeof window!=='undefined') ? (localStorage.getItem('authToken')||'') : '';
    fetch(apiUrl('/api/positions'), { headers: token? { Authorization: `Bearer ${token}` } : {} })
      .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
      .then(({ok, data})=> { if (ok && Array.isArray(data)) setPositionsList(data.map(p=> p.name)); else setPositionsList([]); })
      .catch(()=>{})
  },[]);
  const [regimesList] = useState(["5/2", "6/1", "2/2", "7/0"]);

  // Глобальный выбор даты выплаты (вне таблицы)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerDate, setPickerDate] = useState("");

  // Модалка для полного редактирования комментария
  const [commentModal, setCommentModal] = useState({
    open: false,
    index: null,
    value: "",
  });

  // Формат “ДД месяц ГГГГ” (месяц словами в родительном падеже)
  function formatRuDateVerbose(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = [
      "января",
      "февраля",
      "марта",
      "апреля",
      "мая",
      "июня",
      "июля",
      "августа",
      "сентября",
      "октября",
      "ноября",
      "декабря",
    ];
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = months[d.getMonth()];
    const yyyy = d.getFullYear();
    return `${dd} ${mm} ${yyyy}`;
  }

  // Правила редактирования при блокировке
  const allowCommentEdit = readOnlyLocked ? isSuper : true;       // comment
  const allowDateButton = readOnlyLocked ? isSuper : true;        // "Дата выдачи зп."
  const disableStructureEdits = readOnlyLocked;                   // все остальные поля и операции

  // Надпись “ЗП выдана <дата>” — показываем, если у всех строк одна и та же дата
  const payoutBannerDate = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return "";
    const allDates = rows.map((r) => r.date_cash || "").filter(Boolean);
    if (allDates.length === 0) return "";
    const first = allDates[0];
    const same = allDates.every((d) => d === first);
    return same ? formatRuDateVerbose(first) : "";
  }, [rows]);

  // Числовые utils
  const formatNum = (v) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (isNaN(n)) return "";
    return n.toLocaleString("ru-RU");
  };
  const parseNum = (s, asInt = false) => {
    if (s === null || s === undefined) return 0;
    const cleaned = String(s).replace(/\s/g, "").replace(",", ".");
    const n = asInt ? parseInt(cleaned, 10) : parseFloat(cleaned);
    return isNaN(n) ? 0 : n;
  };
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  // Пересчёт производных
  function recalcDerived(row) {
    const count = Number(row.count) || 0; // 5
    const salary = Number(row.salary) || 0; // 6
    const cash = Number(row.cash_salary) || 0; // 7
    const card = Number(row.salary_card) || 0; // 8

    const cash_total = cash * count; // 9
    const card_total = card * count; // 10
    const total = salary * count; // 11

    const cash_paid = Number(row.cash_paid) || 0; // 12
    const card_paid = Number(row.card_paid) || 0; // 13

    const cash_left = cash_total - cash_paid; // 14
    const card_left = card_total - card_paid; // 15

    return {
      ...row,
      cash_total,
      card_total,
      total,
      cash_left,
      card_left,
    };
  }

  // Валидация: 7 + 8 == 6
  function isSalarySplitValid(row) {
    const salary = Number(row.salary) || 0;
    const cash = Number(row.cash_salary) || 0;
    const card = Number(row.salary_card) || 0;
    return Math.abs(cash + card - salary) < 0.0001;
  }

  // Применить изменения к одной строке (дополнительно поддерживаем черновик)
  function applyRow(idx, mutator, opts) {
    if (idx >= rows.length) {
      setDraftRow((prev) => recalcDerived(mutator({ ...prev })));
      return;
    }
    const next = rows.map((row, i) => {
      if (i !== idx) return row;
      const updated = mutator({ ...row });
      return recalcDerived(updated);
    });
    onChange(next, opts);
  }

  // Применить изменения ко всем строкам
  function applyAll(mutator, opts) {
    const next = rows.map((row) => recalcDerived(mutator({ ...row })));
    onChange(next, opts);
  }

  const commitDraftRow = (opts) => {
    if (disableStructureEdits) return;
    if (!staffRowHasContent(draftRow)) return;
    const nextRow = recalcDerived({ ...draftRow });
    onChange([...rows, nextRow], opts);
    setDraftRow(createEmptyStaffRow());
  };

  const handleDraftRowBlur = (event) => {
    if (disableStructureEdits) return;
    if (!staffRowHasContent(draftRow)) return;
    const stillInside = event?.currentTarget?.contains(event?.relatedTarget);
    if (stillInside) return;
    commitDraftRow({ forceCommit: true });
  };

  // Обработчики

  // 5. Кол-во
  function handleCountChange(idx, valStr) {
    const val = Math.max(0, parseNum(valStr, true));
    applyRow(idx, (row) => ({ ...row, count: val }));
  }

  // 6. З/П на руки: автосплит 70/30, если 7 и 8 не вводились руками
  function handleSalaryChange(idx, valStr) {
    const salary = Math.max(0, parseNum(valStr));
    applyRow(idx, (row) => {
      let cash = Number(row.cash_salary) || 0;
      let card = Number(row.salary_card) || 0;

      const cashManual = !!row._cashManual;
      const cardManual = !!row._cardManual;

      if (!cashManual && !cardManual) {
        cash = round2(salary * 0.7);
        card = round2(salary - cash);
      } else if (cashManual && !cardManual) {
        card = Math.max(0, round2(salary - cash));
      } else if (!cashManual && cardManual) {
        cash = Math.max(0, round2(salary - card));
      }
      return { ...row, salary, cash_salary: cash, salary_card: card };
    });
  }

  // 7. Нал на 1 чел.
  function handleCashChange(idx, valStr) {
    const cash = Math.max(0, parseNum(valStr));
    applyRow(idx, (row) => {
      let card = Number(row.salary_card) || 0;
      const salary = Number(row.salary) || 0;
      if (!row._cardManual) {
        card = Math.max(0, round2(salary - cash));
      }
      return { ...row, cash_salary: cash, salary_card: card, _cashManual: true };
    });
  }

  // 8. На карту на 1 чел.
  function handleCardChange(idx, valStr) {
    const card = Math.max(0, parseNum(valStr));
    applyRow(idx, (row) => {
      let cash = Number(row.cash_salary) || 0;
      const salary = Number(row.salary) || 0;
      if (!row._cashManual) {
        cash = Math.max(0, round2(salary - card));
      }
      return { ...row, salary_card: card, cash_salary: cash, _cardManual: true };
    });
  }

  // 12. Выплачено наличный ФОТ (до подтверждения даты — редактируемое; после — блокируем)
  function handlePaidCashChange(idx, valStr) {
    const cash_paid = Math.max(0, parseNum(valStr));
    applyRow(idx, (row) => ({ ...row, cash_paid }));
  }

  // 13. Выплачено на карту ФОТ
  function handlePaidCardChange(idx, valStr) {
    const card_paid = Math.max(0, parseNum(valStr));
    applyRow(idx, (row) => ({ ...row, card_paid }));
  }

  function handleSimpleChange(idx, field, value) {
    applyRow(idx, (row) => ({ ...row, [field]: value }));
  }

  function handleDeleteRow(idx) {
    const next = rows.filter((_, i) => i !== idx);
    onChange(next, { forceCommit: true });
  }

  // Кнопка “Дата выдачи зп.” (вне таблицы)
  function openGlobalPicker() {
    setPickerDate(pickerDate || new Date().toISOString().slice(0, 10));
    setPickerOpen(true);
  }
  function cancelGlobalPicker() {
    setPickerOpen(false);
  }
  function confirmGlobalPicker() {
    const chosen = pickerDate || new Date().toISOString().slice(0, 10);
    // Проставляем всем строкам и блокируем редактирование "Выплачено"
    applyAll((row) => ({
      ...row,
      date_cash: chosen,
      cash_paid: row.cash_total || 0,
      card_paid: row.card_total || 0,
      _payoutLocked: true,
    }), { forceCommit: true });
    setPickerOpen(false);
  }

  // Видна ли надпись “ЗП выдана ...”
  // const payoutLockedAll = rows.length > 0 && rows.every((r) => !!r._payoutLocked);

  // ====== ИТОГИ (№6 и №10–16) ======
  const isManagerRow = (row) =>
    String(row?.position || "").toLowerCase().includes("менедж");

  const computeTotals = React.useCallback((rows, { excludeManagers = false } = {}) => {
    const filtered = Array.isArray(rows)
      ? rows.filter((r) => (excludeManagers ? !isManagerRow(r) : true))
      : [];

    const sums = {
      count: 0,         // №6
      cash_total: 0,    // №10
      card_total: 0,    // №11
      total: 0,         // №12
      cash_paid: 0,     // №13
      card_paid: 0,     // №14
      cash_left: 0,     // №15
      card_left: 0,     // №16
    };

    for (const r of filtered) {
      sums.count      += Number(r.count) || 0;
      sums.cash_total += Number(r.cash_total) || 0;
      sums.card_total += Number(r.card_total) || 0;
      sums.total      += Number(r.total) || 0;
      sums.cash_paid  += Number(r.cash_paid) || 0;
      sums.card_paid  += Number(r.card_paid) || 0;
      sums.cash_left  += Number(r.cash_left) || 0;
      sums.card_left  += Number(r.card_left) || 0;
    }
    // округление до 2 знаков
    Object.keys(sums).forEach((k) => (sums[k] = round2(sums[k])));
    return sums;
  }, []);

  const sumsNoMgr = useMemo(
    () => computeTotals(rows, { excludeManagers: true }),
    [computeTotals, rows]
  );
  const sumsAll = useMemo(
    () => computeTotals(rows, { excludeManagers: false }),
    [computeTotals, rows]
  );

  // ====== UI: стили ======
  const wrapperStyle = { width: "100%", margin: 0, padding: 0 };

  // Верхняя панель: слева — действия; справа — дата выдачи ЗП
  const toolbarRow = {
    width: "100%",
    marginBottom: 8,
    minHeight: 36,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  };
  const leftActions = {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
  };
  const dateRight = {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  };
  const rightCluster = {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'stretch',
    gap: 16,
    flexWrap: 'wrap',
    flex: '1 1 auto',
  };
  const advancesBox = {
    minWidth: 220,
    maxWidth: 320,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  };
  const advancesLabel = {
    fontSize: 11,
    fontWeight: 700,
    color: '#4b5563',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  };
  const advancesTextarea = {
    minHeight: 64,
    resize: 'vertical',
    borderRadius: 10,
    border: '1px solid rgba(84,39,176,0.25)',
    padding: '8px 10px',
    fontSize: '0.9rem',
    fontFamily: 'inherit',
    background: 'rgba(255,255,255,0.8)',
    outline: 'none',
    lineHeight: 1.4,
    width: '100%',
  };

  const badge = {
    padding: "6px 10px",
    background: "#e7f6d4",
    border: "1px solid #8bc34a",
    borderRadius: 6,
    fontWeight: "bold",
    color: "#2e7d32",
    whiteSpace: "nowrap",
  };

  const tableWrapperStyle = {
    background: "var(--bg-card, #fff)",
    borderRadius: 18,
    border: "1px solid var(--border-200, #e6e8ef)",
    boxShadow: "var(--shadow-1, 0 10px 24px rgba(15,23,42,0.08))",
    overflowX: "auto",
    marginTop: 12,
    position: "relative",
  };
  const sheetTableStyle = {
    borderCollapse: "separate",
    borderSpacing: 0,
    width: "100%",
    tableLayout: "auto",
    borderRadius: 16,
    overflow: "hidden",
    fontSize: "0.92rem",
    minWidth: isCompact ? 960 : 1320,
  };

  const cellStyle = {
    border: "1px solid var(--border-200, #e6e8ef)",
    padding: "6px 4px",
    fontSize: "0.9rem",
    textAlign: "center",
    background: "transparent",
    verticalAlign: "middle",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: 1.25,
  };
  const cellGray = { ...cellStyle, background: "linear-gradient(135deg, var(--brand-50, #f5efff), #f3f4f8)", fontWeight: "bold" };

  // Числовые ячейки — тоже допускаем перенос, но держим выравнивание справа
  const editableRight = {
    ...cellStyle,
    textAlign: "right",
  };
  const editableLeft = { ...cellStyle, textAlign: "left" };
  const readOnlyBox = {
    ...cellGray,
    textAlign: "right",
    paddingRight: 8,
    whiteSpace: "normal",
  };

  const inputNum = {
    width: "100%",
    padding: "6px 2px 4px",
    border: "none",
    borderBottom: "1px solid rgba(84,39,176,0.3)",
    textAlign: "right",
    boxSizing: "border-box",
    fontSize: "0.92rem",
    background: "transparent",
    fontVariantNumeric: "tabular-nums",
  };
  const inputText = {
    width: "100%",
    padding: "6px 2px 4px",
    border: "none",
    borderBottom: "1px solid rgba(84,39,176,0.3)",
    textAlign: "left",
    fontSize: "0.92rem",
    background: "transparent",
  };
  const disabledInput = {
    ...inputNum,
    borderBottom: "1px solid #d1d5db",
    color: "#9ca3af",
    cursor: "not-allowed",
  };

  const btn = {
    padding: "6px 10px",
    border: "1px solid #8bc34a",
    background: "#e7f6d4",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: "bold",
    whiteSpace: "nowrap",
  };
  const btnSecondary = {
    padding: "6px 10px",
    border: "1px solid #ccc",
    background: "#f5f5f5",
    borderRadius: 6,
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  // Итого: стили футера
  const footNum = { ...readOnlyBox, background: "#f5f5f5", fontWeight: 700 };
  const footLabel = {
    ...cellGray,
    background: "#f5f5f5",
    fontWeight: 800,
    textAlign: "left",
  };
  const footEmpty = { background: "#f5f5f5", border: "1px solid #ddd" };

  // ====== Кастомный выпадающий список для Должности ======
  function PositionSelect({ value, onChange, options }) {
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, width: 0, maxHeight: 320 });
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    useEffect(() => {
      function onDocClick(e) {
        if (!open) return;
        const withinButton = containerRef.current && containerRef.current.contains(e.target);
        const withinDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
        if (!withinButton && !withinDropdown) {
          setOpen(false);
        }
      }
      document.addEventListener("mousedown", onDocClick);
      return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    useEffect(() => {
      if (!open || !containerRef.current) return;
      setSearchValue("");
      function updatePosition() {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const desiredWidth = Math.min(Math.max(260, rect.width), viewportWidth - 32);
        let top = rect.bottom + 4;
        const maxHeight = Math.min(420, viewportHeight - 32);
        if (top + maxHeight > viewportHeight - 8 && rect.top > viewportHeight / 2) {
          top = Math.max(8, rect.top - maxHeight - 4);
        }
        const left = Math.min(rect.left, viewportWidth - desiredWidth - 8);
        setMenuStyle({ top, left, width: desiredWidth, maxHeight });
      }
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }, [open]);

    useEffect(() => {
      if (open && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [open]);

    const selectedLabel = value || "";
    const filteredOptions = useMemo(() => {
      const needle = searchValue.trim().toLowerCase();
      if (!needle) return options;
      return options.filter((opt) => opt.toLowerCase().includes(needle));
    }, [options, searchValue]);

    const handlePick = (val) => {
      onChange(val);
      setOpen(false);
    };

    const dropdown = open ? (
      <div
        ref={dropdownRef}
        style={{
          position: "fixed",
          zIndex: 999,
          top: menuStyle.top,
          left: menuStyle.left,
          width: menuStyle.width,
          maxHeight: menuStyle.maxHeight,
          overflowY: "auto",
          background: "#fff",
          border: "1px solid #d1d5db",
          borderRadius: 8,
          boxShadow: "0 16px 40px rgba(15,23,42,0.2)",
        }}
      >
        <div style={{ padding: 10, borderBottom: "1px solid #eee" }}>
          <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 6 }}>Справочник должностей</div>
          <input
            ref={searchInputRef}
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Поиск по должности"
            style={{
              width: "100%",
              padding: "6px 8px",
              borderRadius: 6,
              border: "1px solid #cbd5f5",
            }}
          />
        </div>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePick("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              handlePick("");
            }
          }}
          style={{
            display: "block",
            width: "100%",
            padding: "8px 10px",
            textAlign: "left",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "#475569",
          }}
        >
          — очистить —
        </button>
        {filteredOptions.length === 0 ? (
          <div style={{ padding: 12, color: "#94a3b8", fontSize: 13 }}>Ничего не найдено</div>
        ) : (
          filteredOptions.map((opt, i) => (
            <button
              key={`${opt}-${i}`}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handlePick(opt);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  handlePick(opt);
                }
              }}
              style={{
                display: "block",
                width: "100%",
                padding: "8px 10px",
                textAlign: "left",
                border: "none",
                background: opt === value ? "#e7f6d4" : "transparent",
                cursor: "pointer",
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.25,
              }}
              title={opt}
            >
              {opt}
            </button>
          ))
        )}
        {isAdmin && (
          <div style={{ padding: 10, borderTop: "1px solid #eee", background: "#f8fafc" }}>
            <button
              type="button"
              style={{ ...btn, width: "100%", justifyContent: "center", display: "inline-flex" }}
              onClick={() =>
                onEditPositions
                  ? onEditPositions()
                  : alert("Редактирование справочника должностей")
              }
            >
              Редактировать справочник Должностей
            </button>
          </div>
        )}
      </div>
    ) : null;

    return (
      <div ref={containerRef} style={{ position: "relative", width: "98%" }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          title={selectedLabel}
          style={{
            width: "100%",
            padding: "6px 0",
            border: "none",
            borderBottom: "1px solid rgba(84,39,176,0.3)",
            borderRadius: 0,
            textAlign: "left",
            background: "transparent",
            cursor: "pointer",
            minHeight: 36,
          }}
        >
          <div
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.25,
            }}
          >
            {selectedLabel || "— выберите должность —"}
          </div>
        </button>
        {open && createPortal(dropdown, document.body)}
      </div>
    );
  }

  // ====== Компонент модалки для комментария ======
  function openCommentModal(idx) {
    const isDraft = idx === rows.length;
    setCommentModal({
      open: true,
      index: idx,
      value: isDraft ? draftRow.comment || "" : rows[idx]?.comment || "",
    });
  }
  function closeCommentModal() {
    setCommentModal({ open: false, index: null, value: "" });
  }

  const handleAdvancesInput = (event) => {
    const value = event?.target?.value ?? '';
    setAdvancesDraft(value);
    emitAdvancesChange(value);
  };

  const handleAdvancesBlur = () => {
    emitAdvancesChange(advancesDraft, { forceCommit: true });
  };
  function saveCommentModal() {
    if (commentModal.index === null) return closeCommentModal();
    applyRow(commentModal.index, (row) => ({ ...row, comment: commentModal.value }));
    closeCommentModal();
  }

  // Рендер строки итогов на всю ширину таблицы (18 колонок)
  const COLS_COUNT = 18;
  const LABEL_COL = 2; // колонка "Должность" — сюда пишем «Итого ...»
  function renderTotalsRow(label, sums) {
    const cells = [];
    for (let col = 1; col <= COLS_COUNT; col++) {
      if (col === LABEL_COL) {
        cells.push(
          <td key={`l-${col}`} style={footLabel}>{label}</td>
        );
        continue;
      }
      switch (col) {
        case 6:  cells.push(<td key={`c-${col}`} style={footNum}>{formatNum(sums.count)}</td>); break;
        case 10: cells.push(<td key={`c-${col}`} style={footNum}>{formatNum(sums.cash_total)}</td>); break;
        case 11: cells.push(<td key={`c-${col}`} style={footNum}>{formatNum(sums.card_total)}</td>); break;
        case 12: cells.push(<td key={`c-${col}`} style={footNum}>{formatNum(sums.total)}</td>); break;
        case 13: cells.push(<td key={`c-${col}`} style={footNum}>{formatNum(sums.cash_paid)}</td>); break;
        case 14: cells.push(<td key={`c-${col}`} style={footNum}>{formatNum(sums.card_paid)}</td>); break;
        case 15: cells.push(<td key={`c-${col}`} style={footNum}>{formatNum(sums.cash_left)}</td>); break;
        case 16: cells.push(<td key={`c-${col}`} style={footNum}>{formatNum(sums.card_left)}</td>); break;
        default:
          cells.push(<td key={`e-${col}`} style={footEmpty} />);
      }
    }
    return <tr>{cells}</tr>;
  }

  const PRINT_COLUMNS = [
    { key: "idx", label: "№", value: (_row, idx) => String(idx + 1) },
    { key: "position", label: "Должность", value: (row) => row.position || "" },
    { key: "regime", label: "Режим", value: (row) => row.regime || "" },
    { key: "time_from", label: "Время С", value: (row) => row.time_from || "" },
    { key: "time_to", label: "Время По", value: (row) => row.time_to || "" },
    { key: "count", label: "Кол-во чел. в смену", value: (row) => formatNum(row.count) },
    { key: "salary", label: "З/П на руки на 1 чел.", value: (row) => formatNum(row.salary) },
    { key: "total", label: "Общий ФОТ (Карта+Нал)", value: (row) => formatNum(row.total) },
    { key: "comment", label: "Комментарий", value: (row) => row.comment || "" },
  ];

  function escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  const printPeriodLabel = useMemo(() => {
    const m = Number(printMeta?.periodMonth);
    const y = Number(printMeta?.periodYear);
    if (m && y) {
      const dt = new Date(y, m - 1, 1);
      const month = dt.toLocaleString("ru-RU", { month: "long" });
      return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${y}`;
    }
    if (printMeta?.actualAt) {
      const dt = new Date(printMeta.actualAt);
      if (!Number.isNaN(dt.getTime())) {
        const month = dt.toLocaleString("ru-RU", { month: "long" });
        return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${dt.getFullYear()}`;
      }
    }
    return "—";
  }, [printMeta?.periodMonth, printMeta?.periodYear, printMeta?.actualAt]);

  function buildPrintablePayload(includeManagers) {
    const printableRows = includeManagers ? rows : rows.filter((r) => !isManagerRow(r));
    const totals = includeManagers ? sumsAll : sumsNoMgr;
    const title = includeManagers ? "Печать ФОТ (с менеджером)" : "Печать ФОТ";
    const objectName = printMeta?.objectName || "—";
    const objectCode = printMeta?.objectCode || "—";
    const period = printPeriodLabel;
    return { printableRows, totals, title, objectName, objectCode, period };
  }

  function printFot(includeManagers) {
    const { printableRows, totals, title, objectName, objectCode, period } = buildPrintablePayload(includeManagers);
    const headerHtml = PRINT_COLUMNS.map((col) => `<th>${escapeHtml(col.label)}</th>`).join("");
    const bodyHtml = printableRows
      .map((row, idx) => {
        const cells = PRINT_COLUMNS
          .map((col) => `<td>${escapeHtml(col.value(row, idx))}</td>`)
          .join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");

    const totalRowHtml = `<tr class="total-row">${PRINT_COLUMNS
      .map((col) => {
        if (col.key === "position") return `<td><strong>Итого</strong></td>`;
        if (col.key === "count") return `<td><strong>${escapeHtml(formatNum(totals.count))}</strong></td>`;
        if (col.key === "total") return `<td><strong>${escapeHtml(formatNum(totals.total))}</strong></td>`;
        return "<td></td>";
      })
      .join("")}</tr>`;

    const html = `<!doctype html>
<html lang="ru">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 16px; color: #111827; }
      h1 { font-size: 18px; margin: 0 0 12px; }
      .meta { margin: 0 0 12px; font-size: 13px; line-height: 1.35; }
      .meta strong { display: inline-block; min-width: 120px; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th, td { border: 1px solid #d1d5db; padding: 6px 8px; vertical-align: top; }
      th { background: #f3f4f6; text-align: left; }
      .total-row td { background: #f9fafb; }
      @page { size: portrait; margin: 10mm; }
      html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    </style>
  </head>
  <body>
    <h1>${escapeHtml(title)}</h1>
    <div class="meta">
      <div><strong>Объект:</strong> ${escapeHtml(objectName)}</div>
      <div><strong>Код объекта:</strong> ${escapeHtml(objectCode)}</div>
      <div><strong>Период:</strong> ${escapeHtml(period)}</div>
    </div>
    <table>
      <thead><tr>${headerHtml}</tr></thead>
      <tbody>${bodyHtml}${totalRowHtml}</tbody>
    </table>
  </body>
</html>`;

    // Основной сценарий: печать через скрытый iframe, чтобы не зависеть от блокировки popup.
    try {
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.setAttribute("aria-hidden", "true");
      document.body.appendChild(iframe);

      const cleanup = () => {
        try { document.body.removeChild(iframe); } catch {}
      };

      iframe.onload = () => {
        try {
          const win = iframe.contentWindow;
          if (!win) throw new Error("no_iframe_window");
          win.focus();
          win.print();
          setTimeout(cleanup, 500);
        } catch {
          cleanup();
        }
      };

      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) throw new Error("no_iframe_document");
      doc.open();
      doc.write(html);
      doc.close();
      return;
    } catch {}

    // Fallback: новое окно (если iframe недоступен в окружении браузера)
    const printWindow = window.open("", "_blank", "width=1280,height=900");
    if (!printWindow) {
      alert("Печать заблокирована браузером. Разрешите всплывающие окна для сайта.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }

  function exportFotToExcel(includeManagers) {
    const { printableRows, totals, title, objectName, objectCode, period } = buildPrintablePayload(includeManagers);
    const metaRows = [
      ["Форма", title],
      ["Объект", objectName],
      ["Код объекта", objectCode],
      ["Период", period],
      [],
    ];
    const headerRow = PRINT_COLUMNS.map((col) => col.label);
    const bodyRows = printableRows.map((row, idx) => PRINT_COLUMNS.map((col) => col.value(row, idx)));
    const totalRow = PRINT_COLUMNS.map((col) => {
      if (col.key === "position") return "Итого";
      if (col.key === "count") return formatNum(totals.count);
      if (col.key === "total") return formatNum(totals.total);
      return "";
    });

    const dataForSheet = [...metaRows, headerRow, ...bodyRows, totalRow];
    const ws = XLSX.utils.aoa_to_sheet(dataForSheet);
    ws["!cols"] = PRINT_COLUMNS.map((col) => ({ wch: Math.max(14, String(col.label).length + 2) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ФОТ");

    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const suffix = includeManagers ? "with_manager" : "no_manager";
    XLSX.writeFile(wb, `fot_print_${suffix}_${stamp}.xlsx`);
  }

  return (
    <div style={wrapperStyle}>
      {/* Верхняя панель */}
      <div style={toolbarRow}>
        {/* Слева: действия */}
        <div style={leftActions}>
          <span style={{ fontSize: 13, color: "#6b7280" }}>
            Последняя строка всегда пустая — начните ввод, и она сохранится автоматически.
          </span>

          <button type="button" style={btnSecondary} onClick={() => printFot(false)}>
            Печать ФОТ
          </button>
          <button type="button" style={btnSecondary} onClick={() => printFot(true)}>
            Печать ФОТ с менеджером
          </button>
          <button type="button" style={btnSecondary} onClick={() => exportFotToExcel(false)}>
            Excel ФОТ
          </button>
          <button type="button" style={btnSecondary} onClick={() => exportFotToExcel(true)}>
            Excel ФОТ с менеджером
          </button>

          {isAdmin && (
            <>
              <button
                style={btn}
                onClick={() =>
                  onEditPositions
                    ? onEditPositions()
                    : alert("Открыть редактирование справочника Должностей (доступно только администратору).")
                }
              >
                Редактировать справочник Должностей
              </button>
              <button
                style={btn}
                onClick={() =>
                  onEditRegimes
                    ? onEditRegimes()
                    : alert("Открыть редактирование справочника Режимов (доступно только администратору).")
                }
              >
                Редактировать справочник Режимов
              </button>
            </>
          )}
        </div>

        {/* Справа: поле "Авансы" и дата выдачи */}
        <div style={rightCluster}>
          <div style={advancesBox}>
            <span style={advancesLabel}>Авансы</span>
            <textarea
              value={advancesDraft}
              onChange={handleAdvancesInput}
              onBlur={handleAdvancesBlur}
              style={advancesTextarea}
              placeholder="Комментарий по авансам"
              aria-label="Комментарий по авансам"
            />
          </div>

          <div style={dateRight}>
            {!pickerOpen ? (
              <>
                <button
                  style={{
                    ...btn,
                    opacity: allowDateButton ? 1 : 0.6,
                    cursor: allowDateButton ? 'pointer' : 'not-allowed',
                  }}
                  onClick={allowDateButton ? openGlobalPicker : () => alert('Дата выплаты доступна для редактирования только суперпользователям после подписания')}
                  disabled={!allowDateButton}
                >
                  Дата выдачи зп.
                </button>
                {payoutBannerDate && (
                  <span style={badge}>ЗП выдана {payoutBannerDate}</span>
                )}
              </>
            ) : (
              <>
                <input
                  type="date"
                  value={pickerDate}
                  onChange={(e) => setPickerDate(e.target.value)}
                  style={{ padding: "6px 8px", border: "1px solid #ccc", borderRadius: 6 }}
                />
                <button style={btn} onClick={confirmGlobalPicker}>
                  Ок
                </button>
                <button style={btnSecondary} onClick={cancelGlobalPicker}>
                  Отмена
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Основная таблица штатного расписания */}
      {isCompact && (
        <div className="staff-scroll-hint">Свайпните таблицу по горизонтали, чтобы увидеть все столбцы</div>
      )}
      <div
        style={tableWrapperStyle}
        className={`staff-table-wrapper${isCompact ? " staff-table-wrapper--compact" : ""}`}
      >
        <table style={sheetTableStyle} className="staff-table">
        <thead>
          <tr>
            <th style={cellGray}>№</th>
            <th style={cellGray}>Должность</th>
            <th style={cellGray}>Режим</th>
            <th style={{ ...cellGray, minWidth: 90 }}>Время С</th>
            <th style={{ ...cellGray, minWidth: 90 }}>Время По</th>
            <th style={cellGray}>
              Кол-во<br />чел. в смену
            </th>
            <th style={cellGray}>
              З/П на руки<br />на 1 чел.
            </th>
            <th style={cellGray}>
              Наличная<br />з/п на 1 чел.
            </th>
            <th style={cellGray}>
              На карту з/п<br />в мес.<br />на 1 чел.
            </th>
            <th style={cellGray}>
              Итого наличный<br />ФОТ руб.
            </th>
            <th style={cellGray}>
              Итого на карту<br />ФОТ (на руки)
            </th>
            <th style={cellGray}>
              Общий ФОТ<br />(Карта+Нал)
            </th>
            <th style={cellGray}>
              Выплачено<br />наличный ФОТ
            </th>
            <th style={cellGray}>
              Выплачено<br />на карту ФОТ
            </th>
            <th style={cellGray}>
              Осталось выдать<br />наличный ФОТ
            </th>
            <th style={cellGray}>
              Осталось выдать<br />на карту ФОТ
            </th>
            <th style={{ ...cellGray, minWidth: 160 }}>Комментарий</th>
            <th style={cellGray}></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={18} style={{ ...cellStyle, textAlign: "center", color: "#aaa" }}>
                Нет строк. Заполните последнюю пустую строку ниже — она добавится автоматически.
              </td>
            </tr>
          )}

          {rowsWithDraft.map((row, idx) => {
            const isDraft = idx === rows.length;
            const valid = isSalarySplitValid(row);
            const draftEmpty = isDraft && !staffRowHasContent(row);
            const salaryCellStyle = {
              ...editableRight,
              background: !draftEmpty && !valid ? "#ffeaea" : "#fff",
            };
            const paidLocked = !!row._payoutLocked;
            const canEditPaid = (!readOnlyLocked && !paidLocked) || (readOnlyLocked && isSuper);

            return (
              <tr key={isDraft ? "draft-row" : idx} onBlurCapture={isDraft ? handleDraftRowBlur : undefined}>
                <td style={isDraft ? { ...cellGray, color: "#9aa0ac" } : cellGray}>{idx + 1}</td>

                {/* Должность — custom dropdown с переносом выбранного текста */}
                <td style={{ ...editableLeft }}>
                  {disableStructureEdits ? (
                    <div
                      title={row.position || ''}
                      style={{
                        width: '98%',
                        minHeight: 36,
                        padding: '6px 0',
                        borderBottom: '1px solid #d1d5db',
                        background: 'transparent',
                        color: '#9ca3af',
                        cursor: 'not-allowed',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.25,
                      }}
                    >
                      {row.position || ''}
                    </div>
                  ) : (
                    positionsList.length === 0 ? (
                      <div style={{ fontSize:12, color:'#6b7280' }}>Нет должностей (добавьте их как суперпользователь на странице «Должности»)</div>
                    ) : (
                      <PositionSelect
                        value={row.position || ""}
                        onChange={(val) => handleSimpleChange(idx, "position", val)}
                        options={positionsList}
                      />
                    )
                  )}
                </td>

                {/* Режим — нативный select (редактирование справочника — вверху) */}
                <td>
                  <select
                    value={row.regime || ""}
                    onChange={(e) => !disableStructureEdits && handleSimpleChange(idx, "regime", e.target.value)}
                    disabled={disableStructureEdits}
                    style={{
                      ...inputText,
                      padding: "6px 0 4px",
                      background: disableStructureEdits ? '#f3f4f6' : 'transparent',
                      color: disableStructureEdits ? '#9ca3af' : '#111317',
                    }}
                  >
                    <option value="">---</option>
                    {regimesList.map((r, i) => (
                      <option key={i} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </td>

                {/* Время С */}
                <td style={editableLeft}>
                  <input
                    type="time"
                    value={row.time_from || ""}
                    onChange={(e) => !disableStructureEdits && handleSimpleChange(idx, "time_from", e.target.value)}
                    disabled={disableStructureEdits}
                    style={{
                      ...inputText,
                      textAlign: "center",
                      background: disableStructureEdits ? '#f3f4f6' : 'transparent',
                    }}
                  />
                </td>

                {/* Время По */}
                <td style={editableLeft}>
                  <input
                    type="time"
                    value={row.time_to || ""}
                    onChange={(e) => !disableStructureEdits && handleSimpleChange(idx, "time_to", e.target.value)}
                    disabled={disableStructureEdits}
                    style={{
                      ...inputText,
                      textAlign: "center",
                      background: disableStructureEdits ? '#f3f4f6' : 'transparent',
                    }}
                  />
                </td>

                {/* 5. Кол-во */}
                <td style={editableRight}>
                  <input
                    type="text"
                    value={formatNum(row.count)}
                    onChange={(e) => !disableStructureEdits && handleCountChange(idx, e.target.value)}
                    disabled={disableStructureEdits}
                    inputMode="decimal"
                    pattern="[0-9., ]*"
                    style={disableStructureEdits ? disabledInput : inputNum}
                  />
                </td>

                {/* 6. З/П на руки — проверка суммы */}
                <td style={salaryCellStyle}>
                  <input
                    type="text"
                    value={formatNum(row.salary)}
                    onChange={(e) => !disableStructureEdits && handleSalaryChange(idx, e.target.value)}
                    disabled={disableStructureEdits}
                    inputMode="decimal"
                    pattern="[0-9., ]*"
                    style={disableStructureEdits ? disabledInput : inputNum}
                  />
                  {!draftEmpty && !valid && (
                    <div style={{ color: "#c00", fontSize: 12, marginTop: 4, textAlign: "left" }}>
                      сумма наличной зп и на карту не совпадает с суммой зп на руки. введите корректные значения
                    </div>
                  )}
                </td>

                {/* 7. Наличная з/п */}
                <td style={editableRight}>
                  <input
                    type="text"
                    value={formatNum(row.cash_salary)}
                    onChange={(e) => !disableStructureEdits && handleCashChange(idx, e.target.value)}
                    disabled={disableStructureEdits}
                    inputMode="decimal"
                    pattern="[0-9., ]*"
                    style={disableStructureEdits ? disabledInput : inputNum}
                  />
                </td>

                {/* 8. На карту з/п */}
                <td style={editableRight}>
                  <input
                    type="text"
                    value={formatNum(row.salary_card)}
                    onChange={(e) => !disableStructureEdits && handleCardChange(idx, e.target.value)}
                    disabled={disableStructureEdits}
                    inputMode="decimal"
                    pattern="[0-9., ]*"
                    style={disableStructureEdits ? disabledInput : inputNum}
                  />
                </td>

                {/* 9. Итого наличный ФОТ */}
                <td style={readOnlyBox}>{draftEmpty ? "—" : formatNum(row.cash_total)}</td>

                {/* 10. Итого на карту ФОТ */}
                <td style={readOnlyBox}>{draftEmpty ? "—" : formatNum(row.card_total)}</td>

                {/* 11. Общий ФОТ */}
                <td style={readOnlyBox}>{draftEmpty ? "—" : formatNum(row.total)}</td>

                {/* 12. Выплачено наличный ФОТ (после даты — нередакт.) */}
                <td style={!canEditPaid ? readOnlyBox : editableRight}>
                  <input
                    type="text"
                    value={formatNum(row.cash_paid)}
                    onChange={(e) => canEditPaid && handlePaidCashChange(idx, e.target.value)}
                    style={!canEditPaid ? disabledInput : inputNum}
                    inputMode="decimal"
                    pattern="[0-9., ]*"
                    disabled={!canEditPaid}
                  />
                </td>

                {/* 13. Выплачено на карту ФОТ */}
                <td style={!canEditPaid ? readOnlyBox : editableRight}>
                  <input
                    type="text"
                    value={formatNum(row.card_paid)}
                    onChange={(e) => canEditPaid && handlePaidCardChange(idx, e.target.value)}
                    style={!canEditPaid ? disabledInput : inputNum}
                    inputMode="decimal"
                    pattern="[0-9., ]*"
                    disabled={!canEditPaid}
                  />
                </td>

                {/* 14. Осталось выдать наличный ФОТ */}
                <td style={readOnlyBox}>{draftEmpty ? "—" : formatNum(row.cash_left)}</td>

                {/* 15. Осталось выдать на карту ФОТ */}
                <td style={readOnlyBox}>{draftEmpty ? "—" : formatNum(row.card_left)}</td>

                {/* 16. Комментарий — без прокрутки, кликабельно открывает модалку */}
                <td style={editableLeft}>
                  {allowCommentEdit ? (
                    <button
                      type="button"
                      onClick={() => openCommentModal(idx)}
                      title="Открыть комментарий"
                      style={{
                        width: "95%",
                        minHeight: 36,
                        padding: "6px 0",
                        border: "none",
                        borderBottom: "1px solid rgba(84,39,176,0.3)",
                        borderRadius: 0,
                        textAlign: "left",
                        background: "transparent",
                        cursor: "pointer",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        lineHeight: 1.25,
                      }}
                    >
                      {row.comment ? row.comment : "Комментарий"}
                    </button>
                  ) : (
                    <div
                      title={row.comment || ''}
                      style={{
                        width: '95%',
                        minHeight: 36,
                        padding: '6px 0',
                        border: 'none',
                        borderBottom: '1px solid #d1d5db',
                        textAlign: 'left',
                        background: 'transparent',
                        color: '#9ca3af',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.25,
                        cursor: 'not-allowed',
                      }}
                    >
                      {row.comment || ''}
                    </div>
                  )}
                </td>

                {/* 17. Удаление */}
                <td>
                  {!isDraft && (
                    <button
                      onClick={() => !disableStructureEdits && handleDeleteRow(idx)}
                      style={{ color: disableStructureEdits ? '#999' : "#c00", fontWeight: "bold", background: "none", border: "none", cursor: disableStructureEdits ? 'not-allowed' : "pointer" }}
                      disabled={disableStructureEdits}
                      title={disableStructureEdits ? 'Удаление недоступно: карточка подписана' : "Удалить строку"}
                    >
                      🗑️
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>

        {/* Итоги */}
        <tfoot>
          {renderTotalsRow("Итого без учета менеджера:", sumsNoMgr)}
          {renderTotalsRow("Итого:", sumsAll)}
        </tfoot>
        </table>
      </div>

      {/* Модалка редактирования комментария */}
      {commentModal.open && (
        <div
          onClick={closeCommentModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(800px, 95vw)",
              background: "#fff",
              borderRadius: 10,
              boxShadow: "0 10px 30px rgba(0,0,0,.25)",
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div style={{ fontWeight: "bold", fontSize: 18 }}>
              Комментарий к строке {commentModal.index !== null ? commentModal.index + 1 : ""}
            </div>
            <textarea
              autoFocus
              value={commentModal.value}
              onChange={(e) => setCommentModal((s) => ({ ...s, value: e.target.value }))}
              rows={10}
              style={{
                width: "100%",
                resize: "vertical",
                padding: "10px 12px",
                border: "1px solid #ccc",
                borderRadius: 6,
                fontSize: 16,
                lineHeight: 1.4,
              }}
              placeholder="Введите комментарий"
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={btnSecondary} onClick={closeCommentModal}>
                Отмена
              </button>
              <button style={btn} onClick={saveCommentModal}>
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}